/**
 * Get translated text for a language from a translations array [{ text, language }].
 * @param {Array} translations - Array of { text, language } objects
 * @param {string|number} langId - Language id to match
 * @returns {string}
 */
export const getTranslation = (translations, langId) => {
  if (!translations || !Array.isArray(translations)) return '';
  const langIdStr = langId?.toString?.() || langId;
  for (let i = 0; i < translations.length; i++) {
    const lang = translations[i]?.language;
    const match = lang && (lang._id?.toString?.() === langIdStr || lang === langIdStr || (typeof lang === 'object' && lang._id === langIdStr));
    if (match) {
      const text = translations[i].text;
      if (text && typeof text === 'string') return text;
      return '';
    }
  }
  // Fallback: return first available text if language not matched
  if (translations.length > 0 && translations[0]?.text) {
    const text = translations[0].text;
    if (text && typeof text === 'string') return text;
  }
  return '';
};

/**
 * Display label for a district in selection lists (name may be a string or { text }).
 * @param {{ name?: string | { text?: string } }} item
 * @returns {string}
 */
export const getDistrictListLabel = (item) => {
  if (!item) return "";
  const n = item.name;
  if (typeof n === "string") return n;
  if (n && typeof n === "object" && typeof n.text === "string") return n.text;
  if (n != null && typeof n !== "object") return String(n);
  return "";
};

/**
 * Return true if this item's language matches langId (language may be object with _id or string id).
 */
function matchesLanguageId(item, langId) {
  if (langId == null || langId === '') return false;
  const lang = item?.language;
  if (lang == null) return false;
  const langStr =
    typeof lang === 'object'
      ? lang._id?.toString?.() || lang.toString?.() || ''
      : String(lang);
  return langStr === String(langId);
}

/**
 * Get array of translated strings from array of translation objects.
 * Uses same language matching as getTranslation so string or object language ids work.
 * @param {Array} translations - Array of { text, language } (language may be id string or { _id })
 * @param {string|number} langId - Language id to match
 * @returns {string[]}
 */
export const getTranslationArr = (translations, langId) => {
  const arr = [];
  if (!translations || !Array.isArray(translations)) return arr;
  translations.forEach((con) => {
    if (matchesLanguageId(con, langId) && con?.text != null && con.text !== '') {
      arr.push(typeof con.text === 'string' ? con.text : String(con.text));
    }
  });
  // Fallback only when entries lack language (legacy/malformed data)
  if (arr.length === 0) {
    const hasLanguageField = translations.some((con) => con?.language != null);
    if (!hasLanguageField) {
      translations.forEach((con) => {
        const t = con?.text;
        if (t != null && t !== '') arr.push(typeof t === 'string' ? t : String(t));
      });
    }
  }
  return arr;
};

/** Extract dialable digits for tel: links (matches mobile TextImg behavior). */
export const formatPhoneForTel = (input) => {
  if (!input) return '';
  const numbers = String(input).match(/[+\d]/g);
  return numbers ? numbers.join('') : '';
};

const URL_IN_TEXT_RE = /https?:\/\/[^\s)\]}>,;"']+/gi;
const PHONE_IN_TEXT_RE = /(?:\+\d{1,3}[\s-]?)?(?:\(?\d{2,4}\)?[\s-]?)?[\d(\s\-)]{5,}\d|\b0\d{2,4}[\s-]?\d{4,}\b/g;

const trimUrlTrailingPunctuation = (url) =>
  url.replace(/[.,;:!?)]+$/g, '');

const countDigits = (s) => (String(s).match(/\d/g) || []).length;

/** True when a substring looks like a helpline / phone (not a short number). KEEP IN SYNC with mobile Utilities.js */
export const isLikelyPhoneInText = (raw) => {
  const value = String(raw).trim();
  const digits = countDigits(value);
  if (digits < 7 || digits > 15) return false;
  if (!/^[\d+(]/.test(value.replace(/^\(/, ''))) return false;
  if (/^https?:\/\//i.test(value)) return false;
  return true;
};

/**
 * Split plain guidance/CMS text into text, url, and phone segments for linkified UI.
 * KEEP IN SYNC with HumQadam-mobile-frontend/App/helper/Utilities.js linkifyPlainText
 * @param {string} text
 * @returns {{ type: 'text'|'url'|'phone', value: string, href?: string }[]}
 */
export const linkifyPlainText = (text) => {
  if (text == null || text === '') return [{ type: 'text', value: '' }];
  const str = String(text);
  const matches = [];

  let m;
  const urlRe = new RegExp(URL_IN_TEXT_RE.source, 'gi');
  while ((m = urlRe.exec(str)) !== null) {
    const raw = m[0];
    const value = trimUrlTrailingPunctuation(raw);
    matches.push({
      type: 'url',
      start: m.index,
      end: m.index + raw.length,
      value,
      href: normalizeWebsiteUrl(value),
    });
  }

  const phoneRe = new RegExp(PHONE_IN_TEXT_RE.source, 'g');
  while ((m = phoneRe.exec(str)) !== null) {
    const value = m[0];
    const start = m.index;
    const end = start + value.length;
    if (matches.some((hit) => start < hit.end && end > hit.start)) continue;
    if (!isLikelyPhoneInText(value)) continue;
    const tel = formatPhoneForTel(value);
    if (!tel) continue;
    matches.push({
      type: 'phone',
      start,
      end,
      value,
      href: `tel:${tel}`,
    });
  }

  matches.sort((a, b) => a.start - b.start);
  const hits = [];
  let lastEnd = 0;
  matches.forEach((hit) => {
    if (hit.start >= lastEnd) {
      hits.push(hit);
      lastEnd = hit.end;
    }
  });

  if (hits.length === 0) return [{ type: 'text', value: str }];

  const segments = [];
  let cursor = 0;
  hits.forEach((hit) => {
    if (hit.start > cursor) {
      segments.push({ type: 'text', value: str.slice(cursor, hit.start) });
    }
    segments.push({
      type: hit.type,
      value: hit.value,
      href: hit.href,
    });
    cursor = hit.end;
  });
  if (cursor < str.length) {
    segments.push({ type: 'text', value: str.slice(cursor) });
  }
  return segments;
};

/** Dedupe org email strings from plain string array. */
export const getOrgEmailList = (emails) => {
  if (!emails) return [];
  const list = Array.isArray(emails) ? emails : [emails];
  return [...new Set(list.map((e) => (typeof e === 'string' ? e : e?.text || '').trim()).filter(Boolean))];
};

/** Normalize website URL for href (add https if missing). */
export const normalizeWebsiteUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

/**
 * Organization contact lines for display: filter by active language and dedupe identical numbers.
 * Handles raw API [{ text, language }], Redux-preprocessed string[], or a single value.
 * @param {Array|string|object|null|undefined} contact
 * @param {string|number} langId
 * @returns {string[]}
 */
export const getOrgContactList = (contact, langId) => {
  if (contact == null || contact === "") return [];
  const normalized = Array.isArray(contact) ? contact : [contact];
  if (normalized.every((c) => typeof c === "string")) {
    return [...new Set(normalized.filter(Boolean))];
  }
  const translationObjects = normalized.filter((c) => typeof c === "object" && c != null);
  const strings = normalized.filter((c) => typeof c === "string");
  let texts = [
    ...getTranslationArr(translationObjects, langId),
    ...strings,
  ].filter(Boolean);
  // After full org fetch, contact is [{ text, language }] — if lang id missing/mismatch, avoid blank UI
  if (texts.length === 0 && translationObjects.length > 0) {
    texts = translationObjects
      .map((con) => con?.text)
      .filter((t) => t != null && t !== "")
      .map((t) => (typeof t === "string" ? t : String(t)));
  }
  return [...new Set(texts)];
};

/**
 * Get translated title from an item (e.g. offence, org) that may have title as array, string, or { translations }.
 * @param {object} item - Object with .title (array, string, or { translations }) or .text
 * @param {string|number} langId - Language id for translation lookup
 * @returns {string}
 */
export const getTranslatedItemTitle = (item, langId) => {
  if (!item) return '';
  const title = item.title;
  if (Array.isArray(title)) {
    const t = getTranslation(title, langId);
    if (t) return t;
    if (title[0]?.text && typeof title[0].text === 'string') return title[0].text;
    return '';
  }
  if (typeof title === 'string') return title;
  if (title?.translations && Array.isArray(title.translations)) {
    const t = getTranslation(title.translations, langId);
    if (t) return t;
    return title?.text || '';
  }
  if (title?.text && typeof title.text === 'string') return title.text;
  if (item.text) return item.text;
  return '';
};

/**
 * Get title from appContent by name key.
 * Handles both processed (title = string) and raw API (title = [{ text, language }]) shapes.
 * @param {Array} appContent - The app content array from Redux
 * @param {string} name - The name key to look up (e.g., "Emergency Services")
 * @param {string|number} [langId] - Optional language id when title is raw array; uses first text if omitted
 * @returns {string} The translated title text or empty string
 */
export const getTitleFromAppContent = (appContent, name, langId) => {
  if (!appContent || !Array.isArray(appContent)) return '';
  for (let i = 0; i < appContent.length; i++) {
    if (appContent[i].name === name) {
      const title = appContent[i].title;
      if (typeof title === 'string') return title;
      if (Array.isArray(title)) {
        if (langId != null) return getTranslation(title, langId);
        if (title.length > 0 && title[0]?.text != null) return typeof title[0].text === 'string' ? title[0].text : '';
        return '';
      }
      if (typeof title === 'object' && title !== null) {
        if (typeof title.text === 'object' && title.text !== null) return title.text.text || '';
        return title.text !== undefined ? title.text : '';
      }
      return '';
    }
  }
  return '';
};

/**
 * Log error in development only. Use in Redux thunks for consistent error reporting.
 * @param {Error|*} err
 * @param {string} [context] - e.g. slice name or action type
 */
export const logError = (err, context = "") => {
  if (process.env.NODE_ENV === "development") {
    console.error(context ? `[${context}]` : "", err);
  }
};

export {
  orgServesLocation,
  guidanceStepServesDistrict,
  formatEntityDistrictAndAreasLabel,
  normalizeLocationId,
} from "./locationHelpers";

/**
 * Get content title with support for multiple name variations
 * This is useful when a content name was renamed (e.g., "Referral" -> "Support Providing Organizations")
 * @param {Array} appContent - The app content array from Redux
 * @param {Array<string>} names - Array of name keys to try in order
 * @returns {string} The first found translated title or empty string
 */
export const getContentTitle = (appContent, names) => {
  if (!appContent || !Array.isArray(appContent)) return '';
  if (!names || !Array.isArray(names)) return '';
  
  for (const name of names) {
    const title = getTitleFromAppContent(appContent, name);
    if (title) return title;
  }
  return '';
};
